import * as React from 'react';
import classnames from 'classnames';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import Spinner from 'components/spinner/Spinner';
import SVGIcon from 'components/svg-icon/SVGIcon';

import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { getEntityFromProfile } from 'utils/view';

import type { Campaign } from 'types';
import { SyntheticEvent } from 'react';

applyTranslations(require('./campaigns-multi-select.i18n.json'));

type Props = {
  isLoading: boolean,
  organizationsListIds?: Array<number>,
  campaigns: Array<Campaign>,
  selectedIdsMap: {
    [x: number]: boolean
  },
  onToggle: (a: number) => void
};

class CampaignsMultiSelect extends React.PureComponent<Props> {
  isDisabled(campaign: Campaign): boolean {
    const { organizationsListIds } = this.props;
    if (!organizationsListIds) {
      return false;
    }

    const orgId = campaign.brandProfile.organizationId || 0;

    return campaign.brandProfile ? (organizationsListIds.indexOf(orgId) === -1) : false;
  }

  handleToggle = (event: SyntheticEvent<HTMLElement>): void => {
    const { onToggle } = this.props;
    const campaignId = Number(event.currentTarget.dataset.id);

    onToggle(campaignId);
  };

  renderHeader(): React.ReactNode {
    return (
      <div className="campaigns-multi-select__row campaigns-multi-select__row--heading">
        <div className="campaigns-multi-select__cell">
          {__t('campaigns_multi_select.columns.brand')}
        </div>
        <div className="campaigns-multi-select__cell">
          {__t('campaigns_multi_select.columns.status')}
        </div>
        <div className="campaigns-multi-select__cell">
          {__t('campaigns_multi_select.columns.name')}
        </div>
        <div className="campaigns-multi-select__cell">
          {__t('campaigns_multi_select.columns.dates')}
        </div>
        <div className="campaigns-multi-select__cell">
          {__t('campaigns_multi_select.columns.selected')}
        </div>
      </div>
    );
  }

  renderRow(campaign: Campaign, isSelected: boolean): React.ReactNode {
    const rowClasses = classnames({
      'campaigns-multi-select__row': true,
      'campaigns-multi-select__row--selected': isSelected,
      'campaigns-multi-select__row--disabled': this.isDisabled(campaign)
    });

    const brandEntity = getEntityFromProfile(campaign.brandProfile);

    return (
      <div
        key={`row-${campaign.id}`}
        className={rowClasses}
      >
        <div className="campaigns-multi-select__cell">
          <EntityAvatar
            entity={brandEntity}
            link={brandEntity?.url}
            openInNewTab
            bordered
            customSize={{
              width: 24,
              height: 24
            }}
          />
        </div>
        <div className="campaigns-multi-select__cell">
          <SVGIcon
            name={`campaign-${campaign.state}`}
            width={16}
            height={16}
          />
        </div>
        <div className="campaigns-multi-select__cell campaigns-multi-select__cell--name text-overflow-ellipsis">
          {campaign.name}
        </div>
        <div className="campaigns-multi-select__cell">
          <span className="campaigns-multi-select__start-date">
            {getDisplayDate(campaign.startDate)}
          </span>
          {` - ${getDisplayDate(campaign.endDate)}`}
        </div>
        <div className="campaigns-multi-select__cell">
          <InputCheckbox
            id={`row-checkbox-${campaign.id}-${Math.random()}`}
            data-id={campaign.id}
            checked={isSelected || false}
            onChange={this.handleToggle}
          />
        </div>
      </div>
    );
  }

  render() {
    const { isLoading, campaigns, selectedIdsMap } = this.props;

    return (
      <section className="campaigns-multi-select">
        <div className="campaigns-multi-select__table">
          {this.renderHeader()}
          {
            isLoading ?
              <Spinner
                position="inline"
                size="small"
                color="light"
              /> :
              campaigns.map(campaign =>
                this.renderRow(campaign, selectedIdsMap[campaign.id])
              )
          }
          {
            !isLoading && campaigns.length === 0 &&
              <div className="campaigns-multi-select__empty">
                {__t('campaigns_multi_select.empty')}
              </div>
          }
        </div>
      </section>
    );
  }
}

export default CampaignsMultiSelect;
